import { Skeleton, Stack, Typography } from '@mui/material';
import {
  OLstToken,
  SUPPORTED_CHAINS,
  TLstToken,
  TNativeToken,
} from 'modules/api';
import { useConnection } from 'modules/auth';
import { useGetBtcPriceQuery } from 'modules/common/actions/getBtcPrice';
import { useGetLbtcBalanceQuery } from 'modules/common/actions/getLbtcBalance';
import {
  ACTION_CACHE,
  ACTION_CACHE_LONG,
  DECIMAL_PLACES_BTC,
  DEFAULT_BTC_PRICE,
  USD_DECIMAL_PLACES,
  ZERO,
} from 'modules/common/const';
import { useGlobalTranslation, useTranslation } from 'modules/i18n';
import { default as Diamond } from './assets/diamond.svg?react';
import { useValidChainId } from 'modules/auth/hooks/useChainId';
import { useMemo } from 'react';
import { InfoIconWithTooltip } from '../InfoIconWithTooltip';
import { translation } from './translation';

const lstToken = OLstToken.LBTC;

interface ILbtcBalanceProps {
  showLbtc?: boolean;
  showTokenValue?: boolean;
  showUsd?: boolean;
  valueToken?: TLstToken | TNativeToken;
}

/**
 *
 * @param showLbtc Show LBTC balance
 * @param showTokenValue Show token value
 * @param showUsd Show USD value
 * @param valueToken The token denomination to display the value in
 * @returns
 */
export function LbtcBalance({
  showLbtc = true,
  showTokenValue = true,
  showUsd = true,
  valueToken = lstToken,
}: ILbtcBalanceProps): JSX.Element {
  const { keys } = useTranslation(translation);
  const { t } = useGlobalTranslation();
  const { isConnected } = useConnection();
  const chainId = useValidChainId();

  const { data: btcPriceInUsd = DEFAULT_BTC_PRICE } = useGetBtcPriceQuery(
    undefined,
    { refetchOnMountOrArgChange: ACTION_CACHE_LONG },
  );

  const { data: lbtcBalanceData, isLoading: isLbtcBalanceLoading } =
    useGetLbtcBalanceQuery(undefined, {
      refetchOnMountOrArgChange: ACTION_CACHE,
      skip: !isConnected,
    });

  const chainBalance = lbtcBalanceData?.chains[chainId];

  const balance =
    isConnected && lbtcBalanceData && chainBalance ? chainBalance : ZERO;
  const nativeTokenEquivalent = balance;
  const usdEquivalent = nativeTokenEquivalent.multipliedBy(btcPriceInUsd);

  const tokenValueText = t('unit.tokenValue', {
    token: valueToken,
    value: nativeTokenEquivalent.decimalPlaces(DECIMAL_PLACES_BTC).toFormat(),
  });

  const usdText = t('unit.usdValue', {
    value: usdEquivalent.decimalPlaces(USD_DECIMAL_PLACES).toFormat(),
  });

  const isLbtcSupportedChain = useMemo(
    () => SUPPORTED_CHAINS.includes(chainId),
    [chainId],
  );

  return (
    <Stack gap={0.5}>
      {showLbtc && (
        <Stack
          direction="row"
          alignItems={
            !isConnected || isLbtcBalanceLoading ? 'center' : 'flex-end'
          }
          gap={1.5}
          sx={{
            height: 48,
          }}
        >
          {!isConnected || isLbtcBalanceLoading ? (
            <Skeleton width={48} height={16} />
          ) : (
            <Typography
              variant="h4"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                fontSize: theme => theme.typography.pxToRem(48),
              }}
            >
              {balance.decimalPlaces(DECIMAL_PLACES_BTC).toFormat()}
            </Typography>
          )}

          <Typography
            color="text.secondary"
            sx={{
              fontSize: theme => theme.typography.pxToRem(20),
            }}
          >
            {lstToken}
          </Typography>
          {!isLbtcSupportedChain && (
            <InfoIconWithTooltip
              placement="top-end"
              sx={{
                position: 'relative',
                right: 4,
                bottom: 8,
              }}
            >
              {t(keys.balanceTip)}
            </InfoIconWithTooltip>
          )}
        </Stack>
      )}

      <Typography
        color="text.secondary"
        gap={1.5}
        sx={{
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {showTokenValue && tokenValueText}
        {showTokenValue && showUsd && <Diamond />}
        {showUsd && usdText}
      </Typography>
    </Stack>
  );
}
